import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Brain } from "lucide-react";
import { MODULES } from "@/components/tender360/shared";
import CuadroMando from "@/components/tender360/CuadroMando";
import MarketUniverse from "@/components/tender360/MarketUniverse";
import PipelineView from "@/components/tender360/PipelineView";
import AccountsCRM from "@/components/tender360/AccountsCRM";
import HistoricTenderDB from "@/components/tender360/HistoricTenderDB";
import BidAIKnowledge from "@/components/tender360/BidAIKnowledge";
import CompetitiveIntel from "@/components/tender360/CompetitiveIntel";
import PostmortemRoadmap from "@/components/tender360/PostmortemRoadmap";
import ViabilityPane from "@/components/tender360/ViabilityPane";
import BidWritingPane from "@/components/tender360/BidWritingPane";
import PremiumAlertsSettings from "@/components/tender360/PremiumAlertsSettings";
import KeywordsManager from "@/components/tender360/KeywordsManager";
// Iter 121: el importador Excel maestro se movió a MERCADO. Ver /ofertas-comercial → tab MERCADO.

export default function Tender360() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // N12 (roadmap-valoriza-jev-2026-10-11, Ola 4) · "Mi trabajo y los
  // enlaces de retorno conservan contexto" — antes el tab inicial era
  // siempre "m0" sin leer la URL, así que un enlace desde Mi trabajo a
  // "?tab=bpai" (evidencias M5 sin verificar) aterrizaba igual en el
  // Cuadro de Mando, obligando a buscar el módulo otra vez.
  const [tab, setTab] = useState(() => {
    const valid = MODULES.map(m => m.key);
    const t0 = searchParams.get("tab");
    return t0 && valid.includes(t0) ? t0 : "m0";
  });
  const focusId = searchParams.get("focus");
  const [showAlertsSettings, setShowAlertsSettings] = useState(false);

  useEffect(() => { if (focusId) setTab("m0"); }, [focusId]);

  return (
    <div className="space-y-5 w-full" data-testid="licita-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']" data-testid="tender360-page-title">US_BID360</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t("tender.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAlertsSettings(true)}
            data-testid="tender360-premium-alerts-btn"
            title="Configurar alertas Premium en la campana de notificaciones"
            className="text-xs font-semibold px-3 py-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors inline-flex items-center gap-1.5"
          >
            🔔 Alertas Premium
          </button>
          <div className="flex items-center gap-2 bg-[#007934]/5 border border-[#007934]/20 rounded-xl px-3 py-2">
            <Brain size={14} className="text-[#007934]" />
            <span className="text-xs font-semibold text-[#007934]">Claude Sonnet 4.6</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
        {MODULES.map(m => {
          const Icon = m.icon;
          const active = tab === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setTab(m.key)}
              data-testid={`tab-${m.key}`}
              className={`text-left rounded-xl border p-2.5 transition-all ${active ? "bg-[#007934] text-white border-[#007934] shadow-md" : "bg-white hover:bg-gray-50 text-gray-700 border-gray-200"}`}
            >
              <div className="flex items-center gap-1.5"><Icon size={12}/><span className="text-[11px] font-semibold leading-tight">{m.label}</span></div>
              <div className={`text-[9px] mt-1 ${active ? "text-white/70" : "text-gray-400"}`}>{m.sub}</div>
            </button>
          );
        })}
      </div>

      {tab === "m0"          && <CuadroMando focusExternalId={focusId} />}
      {tab === "market"      && <MarketUniverse />}
      {tab === "pipeline"    && <PipelineView />}
      {tab === "accounts"    && <AccountsCRM />}
      {tab === "historic"    && <HistoricTenderDB />}
      {tab === "bpai"        && <BidAIKnowledge />}
      {tab === "competitive" && <CompetitiveIntel />}
      {tab === "postmortem"  && <PostmortemRoadmap />}
      {tab === "viability"   && <ViabilityPane />}
      {tab === "writing"     && <BidWritingPane />}
      {tab === "keywords"    && <KeywordsManager />}

      {showAlertsSettings && (
        <PremiumAlertsSettings onClose={() => setShowAlertsSettings(false)} />
      )}
    </div>
  );
}
